import type {
  CharacterStay,
  RailBranch,
  RailLayout,
} from "@/data/rail-branches";
import {
  visibleNestedLabel,
  type LocationRun,
} from "../LocationRun/runs";
import { placeGroupsAt } from "../../rail/RailPlaceGroups/groups";

/** Parts of one site that still have someone, skipping a label that repeats the site. */
export function occupiedPartLabels(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  siteLabel: string,
  playheadMs: number,
): string[] {
  const labels = new Set<string>();
  for (const group of placeGroupsAt(stays, rootPlaceId, playheadMs)) {
    const label = group.placeLabel;
    if (label === undefined || label === siteLabel) {
      continue;
    }
    labels.add(label);
  }
  return [...labels].sort((a, b) => a.localeCompare(b));
}

export function partLabelsForRun(
  run: LocationRun,
  branches: readonly RailBranch[],
  stays: readonly CharacterStay[],
  playheadMs: number,
  activePath: readonly string[],
  activeRunId: string | undefined,
): string[] {
  const branch = branches.find((item) => item.id === run.runId);
  if (branch !== undefined) {
    const occupied = occupiedPartLabels(
      stays,
      branch.rootPlaceId,
      branch.label,
      playheadMs,
    );
    if (occupied.length > 0) {
      return occupied;
    }
  }
  // A site's box follows the cast past its first visit, so it keeps naming where they are now.
  const onThisLine = activePath[0] === run.locationLabel && playheadMs >= run.startMs;
  const label = visibleNestedLabel(
    run,
    onThisLine || run.runId === activeRunId,
    activePath,
  );
  return label === null ? [] : [label];
}

export interface PartLabelOffset {
  label: string;
  /** Horizontal shift from the site box, matching the group. */
  dx: number;
}

/** Part names lined up under the people who are still in that part. */
export function partLabelOffsets(
  run: LocationRun,
  rail: RailLayout,
  playheadMs: number,
  activePath: readonly string[],
  activeRunId: string | undefined,
): PartLabelOffset[] {
  const branch = rail.branches.find((item) => item.id === run.runId);
  if (branch !== undefined) {
    return [];
  }
  return partLabelsForRun(
    run,
    rail.branches,
    rail.stays,
    playheadMs,
    activePath,
    activeRunId,
  ).map((label) => ({ label, dx: 0 }));
}

/** Place each part chip under its group. */
export function paintLocationParts(
  stack: HTMLElement,
  parts: readonly PartLabelOffset[],
): void {
  const host = stack.querySelector<HTMLElement>("[data-location-parts]");
  if (host === null) {
    return;
  }
  while (host.childElementCount > parts.length) {
    host.lastElementChild?.remove();
  }
  for (let i = host.childElementCount; i < parts.length; i++) {
    const el = document.createElement("div");
    el.className = "timeline-location-box timeline-location-box--nested";
    el.dataset.boardModel = "LocationSecondary";
    host.append(el);
  }
  parts.forEach((part, index) => {
    const el = host.children[index];
    if (!(el instanceof HTMLElement)) {
      return;
    }
    el.textContent = part.label;
    el.style.left = `calc(50% + ${part.dx.toFixed(1)}px)`;
  });
}
